import { useEffect, useState } from "react";
import { Plus, Trash2, Download, Pencil, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { Switch } from "@/components/ui/switch";
import { FormFields } from "./fields";

const box = "rounded-2xl border border-bt-border bg-white";

export function CategoriesAdmin() {
  const { settings } = useSite();
  const [cats, setCats] = useState([]);
  const [name, setName] = useState("");
  const [area, setArea] = useState(settings.library_areas[0]?.slug || "");
  const load = () => api.get("/categories").then((r) => setCats(r.data));
  useEffect(() => {
    load();
  }, []);
  const add = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    await api.post("/admin/categories", { name: name.trim(), area, order: cats.filter((c) => c.area === area).length });
    setName("");
    load();
  };
  const rename = async (c, n) => {
    if (n && n !== c.name) {
      await api.put(`/admin/categories/${c.id}`, { ...c, name: n });
      toast.success("Renamed");
      load();
    }
  };
  return (
    <div data-testid="admin-categories">
      <form onSubmit={add} className="mb-8 flex flex-col gap-2 sm:flex-row">
        <input className="flex-1 rounded-xl border border-bt-border bg-white px-4 py-3" placeholder="New category name" value={name} onChange={(e) => setName(e.target.value)} data-testid="category-new-name" />
        <select className="rounded-xl border border-bt-border bg-white px-4 py-3" value={area} onChange={(e) => setArea(e.target.value)} data-testid="category-new-area">
          {settings.library_areas.map((a) => <option key={a.slug} value={a.slug}>{a.title}</option>)}
        </select>
        <button className="btn-primary !min-h-[48px]" data-testid="category-add-button"><Plus size={16} /> Add</button>
      </form>
      <div className="space-y-6">
        {settings.library_areas.map((a) => (
          <div key={a.slug}>
            <h3 className="mb-3 text-xl">{a.title}</h3>
            <div className="flex flex-wrap gap-2">
              {cats.filter((c) => c.area === a.slug).map((c) => (
                <span key={c.id} className="inline-flex items-center gap-1 rounded-full border border-bt-border bg-white py-1 pl-3 pr-1 text-sm" data-testid={`category-chip-${c.id}`}>
                  <input defaultValue={c.name} onBlur={(e) => rename(c, e.target.value.trim())} className="w-auto bg-transparent outline-none" size={Math.max(4, c.name.length)} />
                  <button onClick={async () => { await api.delete(`/admin/categories/${c.id}`); load(); }} className="rounded-full p-1.5 text-red-700 hover:bg-red-50" aria-label="Delete category"><Trash2 size={13} /></button>
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const PAGE_SCHEMA = [
  { key: "title", label: "Title" },
  { key: "slug", label: "Slug (page lives at /p/slug)" },
  { key: "body", label: "Content (## for headings, - for bullets)", type: "textarea", rows: 12 },
  { key: "image", label: "Image", type: "image" },
  { key: "seo_title", label: "SEO title" },
  { key: "seo_description", label: "SEO description" },
];

export function PagesAdmin() {
  const [pages, setPages] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/admin/pages").then((r) => setPages(r.data));
  useEffect(() => {
    load();
  }, []);
  const save = async () => {
    try {
      if (edit.id) await api.put(`/admin/pages/${edit.id}`, edit);
      else await api.post("/admin/pages", edit);
      toast.success("Page saved");
      setEdit(null);
      load();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };
  if (edit)
    return (
      <div data-testid="page-editor">
        <div className="mb-6 flex justify-between">
          <button onClick={() => setEdit(null)} className="btn-ghost -ml-3"><ArrowLeft size={16} /> All pages</button>
          <button onClick={save} className="btn-primary !min-h-[44px]" data-testid="page-editor-save">Save page</button>
        </div>
        <FormFields schema={PAGE_SCHEMA} value={edit} onChange={setEdit} prefix="page" />
      </div>
    );
  return (
    <div data-testid="admin-pages">
      <p className="mb-4 text-sm">Create new pages for anything: about, privacy, programmes. Add them to the navigation in Navigation & Footer using the /p/slug link.</p>
      <button onClick={() => setEdit({ title: "", slug: "", body: "", image: "", seo_title: "", seo_description: "" })} className="btn-primary mb-6 !min-h-[44px]" data-testid="admin-pages-new"><Plus size={16} /> New page</button>
      <div className={`${box} divide-y divide-[color:var(--bt-border)]`}>
        {pages.map((p) => (
          <div key={p.id} className="flex items-center justify-between p-4">
            <div><p className="font-semibold text-bt-heading">{p.title}</p><p className="text-sm text-bt-sagedeep">/p/{p.slug}</p></div>
            <div className="flex gap-1">
              <button onClick={() => setEdit(p)} className="rounded-lg p-2 hover:bg-bt-sage" aria-label="Edit" data-testid={`admin-page-edit-${p.slug}`}><Pencil size={16} /></button>
              <button onClick={async () => { if (window.confirm("Delete page?")) { await api.delete(`/admin/pages/${p.id}`); load(); } }} className="rounded-lg p-2 text-red-700 hover:bg-red-50" aria-label="Delete"><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function SubscribersAdmin() {
  const [subs, setSubs] = useState([]);
  const load = () => api.get("/admin/subscribers").then((r) => setSubs(r.data));
  useEffect(() => {
    load();
  }, []);
  const exportCsv = async () => {
    const { data } = await api.get("/admin/subscribers.csv", { responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(data);
    a.download = "better-together-subscribers.csv";
    a.click();
  };
  const active = subs.filter((s) => s.status === "subscribed").length;
  return (
    <div data-testid="admin-subscribers">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm"><b className="font-heading text-2xl text-bt-heading" data-testid="subscriber-count">{active}</b> active subscribers · {subs.length} total</p>
        <button onClick={exportCsv} className="btn-secondary !min-h-[44px] text-sm" data-testid="subscribers-export-csv"><Download size={16} /> Export CSV</button>
      </div>
      <div className={`${box} overflow-x-auto`}>
        <table className="w-full text-left text-sm">
          <thead className="text-xs uppercase tracking-wider text-bt-sagedeep"><tr><th className="p-4">Name</th><th className="p-4">Email</th><th className="p-4">Status</th><th className="p-4">Source</th><th className="p-4">Joined</th><th /></tr></thead>
          <tbody>
            {subs.map((s) => (
              <tr key={s.id} className="border-t border-bt-border" data-testid={`subscriber-row-${s.email}`}>
                <td className="p-4">{s.first_name}</td><td className="p-4">{s.email}</td>
                <td className="p-4"><span className={`rounded-full px-2 py-1 text-xs ${s.status === "subscribed" ? "bg-bt-sage text-bt-primary" : "bg-gray-100"}`}>{s.status}</span></td>
                <td className="p-4">{(s.sources || []).join(", ")}</td><td className="p-4">{s.created_at?.slice(0, 10)}</td>
                <td className="p-4"><button onClick={async () => { await api.delete(`/admin/subscribers/${s.id}`); load(); }} className="text-red-700" aria-label="Delete"><Trash2 size={15} /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
        {!subs.length && <p className="p-6 text-center">No subscribers yet.</p>}
      </div>
      <p className="mt-4 text-xs text-bt-sagedeep">Each subscriber has an unsubscribe link: /unsubscribe?token=&lt;unsubscribe_token&gt; (included in the CSV) to place in your emails.</p>
    </div>
  );
}

export function UsersAdmin() {
  const [users, setUsers] = useState([]);
  useEffect(() => {
    api.get("/admin/users").then((r) => setUsers(r.data));
  }, []);
  const toggle = async (u, v) => {
    const { data } = await api.patch(`/admin/users/${u.id}`, { is_member: v });
    setUsers(users.map((x) => (x.id === u.id ? data : x)));
    toast.success(v ? "Membership granted" : "Membership removed");
  };
  return (
    <div data-testid="admin-users">
      <p className="mb-4 text-sm">Members are added automatically after checkout. You can also grant or remove membership manually.</p>
      <div className={`${box} divide-y divide-[color:var(--bt-border)]`}>
        {users.map((u) => (
          <div key={u.id} className="flex items-center justify-between gap-3 p-4" data-testid={`user-row-${u.email}`}>
            <div className="min-w-0"><p className="truncate font-semibold text-bt-heading">{u.first_name} {u.role === "admin" && <span className="ml-1 text-xs text-bt-sagedeep">(admin)</span>}</p><p className="truncate text-sm text-bt-sagedeep">{u.email}</p></div>
            <label className="flex items-center gap-2 text-sm">Member <Switch checked={!!u.is_member} disabled={u.role === "admin"} onCheckedChange={(v) => toggle(u, v)} data-testid={`user-member-toggle-${u.email}`} /></label>
          </div>
        ))}
      </div>
    </div>
  );
}
